import React, { useState, useRef, useEffect } from "react";
import { Upload, FileText, AlertTriangle, Loader2, X, ArrowLeft } from "lucide-react";
import { PaperCode } from "./types";
import { useAppDispatch } from "../../redux/hooks";
import { uploadToS3 } from "../../redux/commonslice";
import { startEvaluation, updateStrictnessRules } from "./EvaluatorSlice";
import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface EvaluatorSetupProps {
    paper: PaperCode;
    onStartEvaluation: () => void;
    mode?: 'create' | 'edit';
    initialRules?: string;
    showBackButton?: boolean;
}

const DEFAULT_RULES = `- Deduct 1 mark for spelling mistakes.
- Ensure key concepts are explained clearly.
- Award partial marks for correct methodology even if the final answer is wrong.
- Handwriting should be legible; deduct marks for illegible answers.`;

const EvaluatorSetup: React.FC<EvaluatorSetupProps> = ({ paper, onStartEvaluation, mode = 'create', initialRules = '', showBackButton = true }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [file, setFile] = useState<File | null>(null);
    const [, setPreviewUrl] = useState<string | null>(null);
    const [rules, setRules] = useState(initialRules || DEFAULT_RULES);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [viewPdfUrl, setViewPdfUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (initialRules) setRules(initialRules);
    }, [initialRules]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleStartClick = () => {
        if (mode === 'create' && !file && !paper.questionPaperUrl) {
            alert("Please upload a question paper.");
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleConfirmStart = async () => {
        setIsUploading(true);
        try {
            if (file) {
                // 1. Upload File to S3
                const fileUrl = await dispatch(uploadToS3({
                    file,
                    paperCode: paper.code,
                    subjectName: paper.subject
                })).unwrap();

                if (!fileUrl) throw new Error("Upload failed, no URL returned");

                // 2. Register Evaluation (Update Paper)
                await dispatch(startEvaluation({
                    paperId: paper.id,
                    fileUrl,
                    rules: rules || "none"
                })).unwrap();
            } else if (mode === 'edit') {
                // Update Rules Only
                await dispatch(updateStrictnessRules({
                    paperId: paper.id,
                    rules: rules || "none"
                })).unwrap();
            } else if (paper.questionPaperUrl) {
                // Reuse existing question paper
                await dispatch(startEvaluation({
                    paperId: paper.id,
                    fileUrl: paper.questionPaperUrl,
                    rules: rules || "none"
                })).unwrap();
            }

            // 3. Complete
            setShowConfirmDialog(false);
            onStartEvaluation();
        } catch (error: any) {
            console.error("Failed to process:", error);
            setShowConfirmDialog(false);
            const message = typeof error === 'string' ? error : error?.message || "Failed to update configuration. Please try again.";
            toast.error(message);
        } finally {
            setIsUploading(false);
        }
    };

    const isEdit = mode === 'edit';

    // Drag and Drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFile = e.dataTransfer.files[0];
            const extension = droppedFile.name.split('.').pop()?.toLowerCase();

            if (extension === 'pdf' || droppedFile.type === 'application/pdf') {
                setFile(droppedFile);
                setPreviewUrl(URL.createObjectURL(droppedFile));
            } else {
                alert('Please upload a PDF file only.');
            }
        }
    };

    return (
        <div className="w-full max-w-7xl mx-auto flex flex-col gap-6 h-full">
            {showBackButton && (
                <Button
                    variant="outline"
                    onClick={() => navigate('/evaluator')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 w-fit px-3 py-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-all hover:shadow-sm"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to Dashboard
                </Button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 md:min-h-[500px]">
                {/* Upload Section */}
                <div className="tour-setup-paper bg-card p-6 md:p-8 rounded-xl border border-border shadow-sm flex flex-col h-auto md:h-full">
                    <h3 className="text-lg font-bold text-card-foreground mb-6 flex items-center gap-2 shrink-0">
                        <Upload className="w-5 h-5 text-primary" />
                        {isEdit ? "Update Question Paper" : "Question Paper"}
                    </h3>

                    <div
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className="border-2 border-dashed border-border rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 hover:border-primary/50 transition-all group flex-1 w-full min-h-[250px]"
                    >
                        {file ? (
                            <div className="text-center">
                                <div className="p-4 bg-muted rounded-full mb-4 mx-auto w-fit group-hover:bg-primary/10 transition-colors">
                                    <FileText className="w-8 h-8 text-primary" />
                                </div>
                                <p className="text-sm font-semibold text-foreground">{file.name}</p>
                                <p className="text-xs text-muted-foreground mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFile(null);
                                        setPreviewUrl(null);
                                    }}
                                    className="text-xs text-destructive hover:text-destructive/80 font-medium mt-3 underline"
                                >
                                    Remove & Use Existing
                                </button>
                            </div>
                        ) : paper.questionPaperUrl ? (
                            <div className="text-center relative w-full h-full flex flex-col items-center justify-center" onClick={() => {
                                // If clicking the "view" button don't trigger upload
                                // actually the whole div triggers upload, so we need detailed handling or move the view button out/stop prop
                            }}>
                                <div className="p-4 bg-green-50 rounded-full mb-4 mx-auto w-fit">
                                    <FileText className="w-8 h-8 text-green-600" />
                                </div>
                                <p className="text-sm font-semibold text-foreground mb-1">Current Question Paper Available</p>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (paper.questionPaperUrl) setViewPdfUrl(paper.questionPaperUrl);
                                    }}
                                    className="text-xs text-primary hover:underline mb-4 block"
                                >
                                    View Current PDF
                                </a>
                                <p className="text-xs text-muted-foreground">Click area to replace with new file</p>
                            </div>
                        ) : (
                            <div className="text-center">
                                <div className="p-4 bg-muted rounded-full mb-4 mx-auto w-fit group-hover:bg-primary/10 transition-colors">
                                    <FileText className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
                                </div>
                                <p className="text-sm font-medium text-foreground">Click to upload or drag & drop PDF</p>
                                <p className="text-xs text-muted-foreground mt-1">Supports PDF (Max 10MB)</p>
                            </div>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept=".pdf,application/pdf"
                            onChange={handleFileChange}
                        />
                    </div>
                </div>

                {/* Rules Section */}
                <div className="tour-setup-rules bg-card p-6 md:p-8 rounded-xl border border-border shadow-sm flex flex-col h-auto md:h-full">
                    <h3 className="text-lg font-bold text-card-foreground mb-2 flex items-center gap-2 shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                        Evaluation Rules
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6 shrink-0">Define the marking scheme, key points to look for, and strictness levels.</p>

                    <textarea
                        className="w-full flex-1 min-h-[300px] p-4 bg-muted/30 border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none text-sm text-foreground placeholder:text-muted-foreground font-mono leading-relaxed"
                        placeholder="- Deduct 1 mark for spelling errors&#10;- Strict checking on diagrams&#10;- Key terms required: 'Photosynthesis', 'Chlorophyll'"
                        value={rules}
                        onChange={(e) => setRules(e.target.value)}
                    />
                </div>
            </div>

            {/* Bottom Action Bar */}
            <div className="flex justify-end pt-2 mt-10 shrink-0">
                <button
                    onClick={handleStartClick}
                    className="tour-setup-start px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all active:scale-[0.98] flex items-center gap-2"
                >
                    {isEdit ? "Update Configuration" : "Start Evaluation"}
                </button>
            </div>

            {/* Confirmation Dialog Overlay */}
            {showConfirmDialog && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-xl">
                    <div className="bg-card p-6 rounded-2xl w-full max-w-md shadow-2xl border border-border animate-in fade-in zoom-in duration-200 m-4">
                        <div className="flex items-center gap-4 mb-4 text-orange-600">
                            <div className="p-3 bg-orange-100 rounded-full">
                                <AlertTriangle className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-card-foreground">{isEdit ? "Update Settings?" : "Start Evaluation?"}</h3>
                        </div>

                        <p className="text-muted-foreground mb-6 leading-relaxed">
                            {isEdit
                                ? "This will update the evaluation criteria for future answer sheets. Existing evaluations might not be affected."
                                : <span>Once started, you <span className="font-bold text-foreground">cannot</span> change the question paper or modify the evaluation rules. Please verify all details before proceeding.</span>
                            }
                        </p>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                disabled={isUploading}
                                className="px-5 py-2.5 rounded-lg font-medium text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmStart}
                                disabled={isUploading}
                                className="px-5 py-2.5 rounded-lg font-bold text-primary-foreground bg-primary hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                                {isUploading ? "Processing..." : (isEdit ? "Confirm Update" : "Confirm & Start")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Viewer Modal */}
            {viewPdfUrl && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => setViewPdfUrl(null)}>
                    <div className="bg-white rounded-2xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="font-semibold text-lg">Question Paper Viewer</h3>
                            <button onClick={() => setViewPdfUrl(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>
                        <div className="flex-1 bg-gray-100 p-0">
                            <iframe src={viewPdfUrl} className="w-full h-full" title="PDF Viewer" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EvaluatorSetup;
